import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  Home, 
  Download,
  Loader2,
  Calendar,
  User,
  FlaskConical,
  DollarSign,
  CreditCard,
  FileText
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PagoExitoso() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solicitudData, setSolicitudData] = useState(null);
  const [cotizacion, setCotizacion] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('clinica_token');

  useEffect(() => {
    if (!token) {
      navigate('/clinica/login');
      return;
    }
    
    if (sessionId) {
      completeCheckout();
    } else {
      setError('No se encontró la sesión de pago');
      setIsLoading(false);
    }
  }, [sessionId, token]);

  const completeCheckout = async () => {
    try {
      // Complete the checkout and create the solicitud
      const response = await axios.post(
        `${API}/payments/complete-checkout/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSolicitudData(response.data);
      
      // Fetch cotizacion details
      if (response.data.solicitud_id) {
        const cotizacionRes = await axios.get(
          `${API}/solicitudes/${response.data.solicitud_id}/cotizacion`
        );
        setCotizacion(cotizacionRes.data);
      }
    } catch (error) {
      console.error('Error completing checkout:', error);
      if (error.response?.data?.detail?.includes('ya procesado')) {
        // Already processed, try to get the solicitud
        setError('Esta solicitud ya fue procesada anteriormente.');
      } else {
        setError(error.response?.data?.detail || 'Error al procesar el pago');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#505BB3] mx-auto mb-4" />
          <p className="text-slate-600">Procesando su pago...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100">
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
            </div>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/clinica/dashboard')}>
              Volver al Panel
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Success Header */}
          <div className="bg-green-500 px-6 py-8 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              ¡Pago Completado Exitosamente!
            </h1>
            <p className="text-white/90">
              Su pago ha sido procesado y su solicitud ha sido registrada
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* ID Banner */}
            {(solicitudData || cotizacion) && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <p className="text-blue-600 text-sm font-medium mb-1">ID DE SOLICITUD</p>
                <p className="text-blue-900 text-2xl font-bold font-mono tracking-wider">
                  {solicitudData?.id_corto || cotizacion?.id_corto || `SOL-${(solicitudData?.solicitud_id || cotizacion?.solicitud_id || '').slice(0, 6).toUpperCase()}`}
                </p>
              </div>
            )}
            
            {/* Payment Confirmation */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-green-600" />
                <div>
                  <h4 className="font-semibold text-green-900">Pago Confirmado</h4>
                  <p className="text-sm text-green-700">
                    {sessionId && <span>Referencia: {sessionId.slice(0, 20)}...</span>}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-2xl font-bold text-green-600">
                    ${solicitudData?.total?.toFixed(2) || cotizacion?.total?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-green-600">MXN</p>
                </div>
              </div>
            </div>

            {/* Cotización Details */}
            {cotizacion && (
              <>
                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Fecha
                    </div>
                    <p className="font-semibold text-slate-900">{cotizacion.fecha}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <User className="w-4 h-4" />
                      Paciente
                    </div>
                    <p className="font-semibold text-slate-900">{cotizacion.paciente}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <FlaskConical className="w-4 h-4" />
                      Pruebas
                    </div>
                    <p className="font-semibold text-slate-900">{cotizacion.items?.length || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Total
                    </div>
                    <p className="font-semibold text-green-600">${cotizacion.total?.toFixed(2)} MXN</p>
                  </div>
                </div>

                {/* Pruebas List */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Pruebas Solicitadas</h3>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {cotizacion.items?.map((item, index) => (
                      <div key={index} className={`px-4 py-3 ${item.tipo === 'escalonado' ? 'bg-green-50' : ''}`}>
                        {item.tipo === 'escalonado' ? (
                          <div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-green-600">📦</span>
                                <span className="font-medium text-slate-900">{item.nombre}</span>
                              </div>
                              <span className="font-semibold text-green-600">${item.precio?.toFixed(2)}</span>
                            </div>
                            {item.pruebas_incluidas && (
                              <div className="mt-2 pl-6 space-y-1">
                                {item.pruebas_incluidas.map((p, i) => (
                                  <p key={i} className="text-sm text-slate-600">• {p}</p>
                                ))}
                                <p className="text-xs text-green-600 mt-1">
                                  Precio escalonado aplicado
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-slate-900">{item.nombre}</span>
                              {item.codigo && <span className="text-sm text-slate-500 ml-2">({item.codigo})</span>}
                            </div>
                            <span className="font-semibold text-slate-700">${item.precio?.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="bg-green-50 px-4 py-3 border-t border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-green-900">TOTAL PAGADO</span>
                      <span className="text-xl font-bold text-green-600">${cotizacion.total?.toFixed(2)} MXN</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Próximos Pasos:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Recibirá un correo de confirmación con los detalles de su solicitud</li>
                <li>• Nuestro equipo procesará las muestras y realizará las pruebas</li>
                <li>• Los resultados estarán disponibles en su panel en aproximadamente 24-48 horas</li>
                <li>• Recibirá una notificación cuando los resultados estén listos</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => navigate('/clinica/dashboard')}
                className="flex-1 h-12 bg-[#505BB3] hover:bg-[#404A9C] text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Volver al Panel
              </Button>
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="flex-1 h-12 border-slate-300"
              >
                <Download className="w-4 h-4 mr-2" />
                Imprimir Comprobante
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
